/**
 * Multilingual editor — a text input / textarea with per-language tabs.
 * Values are stored as an object keyed by language code: { en: "...", es: "..." }.
 *
 * Usage:
 *   const [val, setVal] = useState({ en: "" });
 *   <MultilingualEditor value={val} onChange={setVal} label="Full name" />
 */
import { useEffect, useState } from "react";
import { Plus, Languages, X, Search } from "lucide-react";
import { LANGUAGES } from "@/lib/ml";

// Full catalogue: international + all 22 scheduled Indian languages (+ Bhojpuri).
const COMMON = LANGUAGES;

export default function MultilingualEditor({
  value,
  onChange,
  label,
  placeholder,
  as = "input", // 'input' | 'textarea'
  rows = 3,
  maxLength,
  testid,
}) {
  const initial = value && typeof value === "object" ? value : { en: value || "" };
  const keys = Object.keys(initial).length > 0 ? Object.keys(initial) : ["en"];
  const [active, setActive] = useState(keys[0]);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");

  // Keep the active tab valid if the value's language set changes under us
  // (parent remounts, language removed, etc.) so typing never lands in the
  // wrong language.
  useEffect(() => {
    if (!keys.includes(active)) setActive(keys[keys.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys.join("|")]);

  const langs = keys.map((k) => COMMON.find((l) => l.code === k) || { code: k, label: k.toUpperCase() });
  const available = COMMON.filter((l) => !keys.includes(l.code));
  const q = pickerQuery.trim().toLowerCase();
  const filtered = q
    ? available.filter(
        (l) =>
          l.label.toLowerCase().includes(q) ||
          (l.name || "").toLowerCase().includes(q) ||
          l.code.toLowerCase().startsWith(q)
      )
    : available;

  function updateField(code, v) {
    onChange({ ...initial, [code]: v });
  }
  function addLang(code) {
    onChange({ ...initial, [code]: "" });
    setActive(code);
    setShowPicker(false);
    setPickerQuery("");
  }
  function removeLang(code) {
    if (code === "en") return; // never remove the base
    const next = { ...initial };
    delete next[code];
    onChange(next);
    if (active === code) setActive("en");
  }

  const Input = as === "textarea" ? "textarea" : "input";

  return (
    <div data-testid={testid}>
      {label && <div className="overline mb-1">{label}</div>}
      <div className="flex flex-wrap items-center gap-1 mb-2">
        {langs.map((l) => (
          <button
            type="button"
            key={l.code}
            onClick={() => setActive(l.code)}
            data-testid={testid ? `${testid}-tab-${l.code}` : undefined}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-heritage-caps tracking-[0.14em] border ${
              active === l.code
                ? "bg-[color:var(--sepia)] text-[color:var(--parchment)] border-[color:var(--sepia)]"
                : "border-[color:var(--sepia)]/40 text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)]"
            }`}
          >
            {l.label}
            {l.code !== "en" && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  removeLang(l.code);
                }}
                className="ml-1 opacity-60 hover:opacity-100"
                title={`Remove ${l.label}`}
              >
                <X size={10} />
              </span>
            )}
          </button>
        ))}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowPicker((s) => !s)}
            data-testid={testid ? `${testid}-add-lang` : undefined}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border border-dashed border-[color:var(--sepia)]/50 text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)]"
          >
            <Plus size={10} /> Language
          </button>
          {showPicker && (
            <div className="absolute top-full left-0 mt-1 z-20 bg-[color:var(--parchment)] border border-[color:var(--sepia)]/40 shadow-lg min-w-[200px]">
              <div className="p-2 border-b border-[color:var(--sepia)]/20 flex items-center gap-1.5 sticky top-0 bg-[color:var(--parchment)]">
                <Search size={11} className="text-[color:var(--sepia)] shrink-0" />
                <input
                  data-testid={testid ? `${testid}-pick-search` : undefined}
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Find a language…"
                  className="w-full bg-transparent text-xs focus:outline-none placeholder:text-[color:var(--sepia)]/50"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="p-3 text-xs text-[color:var(--ink-soft)]">
                    {available.length === 0 ? "All added" : "No match"}
                  </div>
                ) : (
                  filtered.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => addLang(l.code)}
                      data-testid={testid ? `${testid}-pick-${l.code}` : undefined}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-[color:var(--parchment-2)]"
                    >
                      {l.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Input
        data-testid={testid ? `${testid}-input` : undefined}
        lang={active}
        value={initial[active] || ""}
        onChange={(e) => updateField(active, e.target.value)}
        placeholder={placeholder}
        rows={as === "textarea" ? rows : undefined}
        maxLength={maxLength}
        className={`w-full bg-transparent ${
          as === "textarea"
            ? "border border-[color:var(--sepia)]/40 p-3 resize-none"
            : "border-b border-[color:var(--sepia)]/50 py-2"
        } focus:outline-none focus:border-[color:var(--rust)]`}
      />

      <div className="mt-1 flex items-center justify-between numeric-index text-[color:var(--ink-soft)]">
        <span>
          <Languages size={10} className="inline mr-1" />
          {langs.length} language{langs.length === 1 ? "" : "s"}
        </span>
        {maxLength && (
          <span>
            {(initial[active] || "").length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
}
